import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AssignmentTurnedIn from '@mui/icons-material/esm/AssignmentTurnedIn';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import Close from '@mui/icons-material/esm/Close';
import Refresh from '@mui/icons-material/esm/Refresh';
import Verified from '@mui/icons-material/esm/Verified';
import { PageHeader } from '../../components/layout';
import qcChecklistService, { QcItemResultValue, QcRun } from '../../services/api/qcChecklistService';
import {
  cachedQcRunById,
  cacheQcRun,
  enqueueOfflineQcItemResult,
  enqueueOfflineQcRunPatch,
  qcOverallResultFor,
} from '../../services/offline/fieldOfflineForms';

const labelize = (value?: string | null) => value ? value.replace(/_/g, ' ') : '-';
const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;
const isNetworkFailure = (err: any) => !err?.response;
const shouldQueueOffline = (fieldMode: boolean, err: any) => fieldMode && (isOffline() || isNetworkFailure(err));

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  if (status === 'signed_off') return 'success';
  if (status === 'completed') return 'info';
  if (status === 'voided') return 'default';
  return 'warning';
};

const resultColor = (result?: string | null): 'success' | 'warning' | 'error' | 'default' => {
  if (result === 'pass') return 'success';
  if (result === 'pass_with_observations') return 'warning';
  if (result === 'fail') return 'error';
  return 'default';
};

interface QcRunPageProps {
  fieldMode?: boolean;
}

export default function QcRunPage({ fieldMode = false }: QcRunPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<QcRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [attestation, setAttestation] = useState('I attest that this QC checklist run is accurate and complete.');
  const [voidReason, setVoidReason] = useState('');

  const loadRun = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      if (fieldMode && isOffline()) {
        const cached = await cachedQcRunById(id);
        if (cached) {
          setRun(cached);
          setNotice('Showing cached QC run. Changes will sync when you reconnect.');
          return;
        }
      }
      const serverRun = await qcChecklistService.getRun(id);
      setRun(serverRun);
      if (fieldMode) {
        await cacheQcRun(serverRun);
      }
    } catch (err: any) {
      if (fieldMode) {
        const cached = await cachedQcRunById(id);
        if (cached) {
          setRun(cached);
          setNotice('Showing cached QC run. Changes will sync when you reconnect.');
          return;
        }
      }
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load QC run');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const resultByIndex = useMemo(() => {
    const map = new Map<number, NonNullable<QcRun['itemResults']>[number]>();
    (run?.itemResults ?? []).forEach((result) => map.set(result.itemIndex, result));
    return map;
  }, [run]);

  const completeReady = run ? run.itemsSnapshot.every((_item, index) => Boolean(resultByIndex.get(index)?.result)) : false;
  const evidenceReady = run ? run.itemsSnapshot.every((item, index) => {
    if (!item.requiresEvidence) return true;
    const result = resultByIndex.get(index);
    return Boolean(result?.evidenceDocumentId || result?.evidenceFieldId);
  }) : false;
  const isEditable = run?.status === 'in_progress';

  const saveItem = async (index: number, patch: { result?: QcItemResultValue; notes?: string | null; evidenceDocumentId?: string | null; evidenceFieldId?: string | null }) => {
    if (!run) return;
    const current = resultByIndex.get(index);
    const payload = {
      result: patch.result ?? current?.result ?? 'pass',
      notes: patch.notes ?? current?.notes ?? null,
      evidenceDocumentId: patch.evidenceDocumentId ?? current?.evidenceDocumentId ?? null,
      evidenceFieldId: patch.evidenceFieldId ?? current?.evidenceFieldId ?? null,
    };
    try {
      if (fieldMode && isOffline()) {
        throw new Error('offline');
      }
      await qcChecklistService.recordItemResult(run.id, index, payload);
      await loadRun();
    } catch (err: any) {
      if (shouldQueueOffline(fieldMode, err)) {
        setRun(await enqueueOfflineQcItemResult(run, index, payload));
        setNotice('QC item saved offline. It will sync when you reconnect.');
        setError(null);
        return;
      }
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save item result');
    }
  };

  const completeRun = async () => {
    if (!run) return;
    try {
      if (fieldMode && isOffline()) {
        throw new Error('offline');
      }
      const completed = await qcChecklistService.completeRun(run.id);
      setRun(completed);
      if (fieldMode) await cacheQcRun(completed);
    } catch (err: any) {
      if (shouldQueueOffline(fieldMode, err)) {
        const completedAt = new Date().toISOString();
        const overallResult = qcOverallResultFor(run.itemResults ?? []);
        setRun(await enqueueOfflineQcRunPatch(
          run,
          { status: 'completed', overallResult, completedAt },
          { status: 'completed', overallResult, completedAt }
        ));
        setNotice('QC run completed offline. It will sync when you reconnect.');
        setError(null);
        return;
      }
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to complete QC run');
    }
  };

  const signRun = async () => {
    if (!run) return;
    try {
      if (fieldMode && isOffline()) {
        throw new Error('offline');
      }
      const signed = await qcChecklistService.signRun(run.id, { attestation });
      setRun(signed);
      if (fieldMode) await cacheQcRun(signed);
    } catch (err: any) {
      if (shouldQueueOffline(fieldMode, err)) {
        setRun(await enqueueOfflineQcRunPatch(
          run,
          { status: 'signed_off', notes: attestation },
          { status: 'signed_off', notes: attestation }
        ));
        setNotice('QC run signed offline. It will sync when you reconnect.');
        setError(null);
        return;
      }
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to sign QC run');
    }
  };

  const voidRun = async () => {
    if (!run || !voidReason.trim()) return;
    try {
      if (fieldMode && isOffline()) {
        throw new Error('offline');
      }
      const voided = await qcChecklistService.voidRun(run.id, voidReason);
      setRun(voided);
      if (fieldMode) await cacheQcRun(voided);
    } catch (err: any) {
      if (shouldQueueOffline(fieldMode, err)) {
        setRun(await enqueueOfflineQcRunPatch(
          run,
          { status: 'voided', notes: voidReason },
          { status: 'voided', notes: voidReason }
        ));
        setNotice('QC run voided offline. It will sync when you reconnect.');
        setError(null);
        return;
      }
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to void QC run');
    }
  };

  if (loading && !run) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (!run) {
    return (
      <Box>
        <PageHeader title="QC Run" />
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={run.runCode}
        subtitle={`${run.template?.name ?? 'QC checklist'} - version ${run.templateVersion}`}
        actions={
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <Button variant="outlined" startIcon={<Refresh />} onClick={loadRun}>Refresh</Button>
            </Tooltip>
            <Button variant="outlined" onClick={() => navigate(`${fieldMode ? '/field/tasks' : '/tasks'}/${run.taskId}`)}>Task</Button>
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Paper sx={{ p: 2, borderRadius: 1, mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Chip label={labelize(run.status)} color={statusColor(run.status)} />
          {run.overallResult && <Chip label={labelize(run.overallResult)} color={resultColor(run.overallResult)} />}
          <Chip variant="outlined" label={run.task?.taskCode ?? run.taskId} component={RouterLink} to={`${fieldMode ? '/field/tasks' : '/tasks'}/${run.taskId}`} clickable />
          <Chip variant="outlined" label={run.task?.taskType ? labelize(run.task.taskType) : 'Task type unknown'} />
        </Stack>
      </Paper>

      <Stack spacing={1.5}>
        {run.itemsSnapshot.map((item, index) => {
          const result = resultByIndex.get(index);
          return (
            <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 150px 1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>{index + 1}. {item.text}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    {item.critical && <Chip size="small" color="error" label="critical" />}
                    {item.requiresEvidence && <Chip size="small" color="info" label="evidence" />}
                  </Stack>
                </Box>
                <TextField select size="small" label="Result" value={result?.result ?? ''} disabled={!isEditable} onChange={(event) => saveItem(index, { result: event.target.value as QcItemResultValue })}>
                  <MenuItem value="pass">pass</MenuItem>
                  <MenuItem value="fail">fail</MenuItem>
                  <MenuItem value="na">n/a</MenuItem>
                </TextField>
                <TextField
                  key={`notes-${result?.id ?? index}-${result?.notes ?? ''}`}
                  size="small"
                  label="Notes"
                  defaultValue={result?.notes ?? ''}
                  disabled={!isEditable}
                  onBlur={(event) => saveItem(index, { notes: event.target.value })}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    key={`doc-${result?.id ?? index}-${result?.evidenceDocumentId ?? ''}`}
                    size="small"
                    label="Document ID"
                    defaultValue={result?.evidenceDocumentId ?? ''}
                    disabled={!isEditable}
                    onBlur={(event) => saveItem(index, { evidenceDocumentId: event.target.value || null })}
                  />
                  <TextField
                    key={`field-${result?.id ?? index}-${result?.evidenceFieldId ?? ''}`}
                    size="small"
                    label="Field Evidence ID"
                    defaultValue={result?.evidenceFieldId ?? ''}
                    disabled={!isEditable}
                    onBlur={(event) => saveItem(index, { evidenceFieldId: event.target.value || null })}
                  />
                </Stack>
              </Box>
            </Paper>
          );
        })}
      </Stack>

      <Paper sx={{ p: 2, borderRadius: 1, mt: 2 }}>
        <Stack spacing={2}>
          {run.status === 'in_progress' && (
            <Tooltip title={completeReady ? '' : 'Every checklist item needs a result'}>
              <span>
                <Button variant="contained" startIcon={<AssignmentTurnedIn />} disabled={!completeReady} onClick={completeRun}>
                  Complete
                </Button>
              </span>
            </Tooltip>
          )}

          {run.status === 'completed' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 1.5 }}>
              <TextField label="Attestation" value={attestation} onChange={(event) => setAttestation(event.target.value)} />
              <Tooltip title={evidenceReady ? '' : 'Required evidence is missing'}>
                <span>
                  <Button variant="contained" startIcon={<Verified />} disabled={!evidenceReady || attestation.trim().length < 10} onClick={signRun}>
                    Sign
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}

          {run.signatures && run.signatures.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Signatures</Typography>
              <Stack spacing={1}>
                {run.signatures.map((signature) => (
                  <Chip key={signature.id} icon={<CheckCircle />} label={`${signature.roleAtSigning} - ${new Date(signature.signedAt).toLocaleString()}`} sx={{ justifyContent: 'flex-start' }} />
                ))}
              </Stack>
            </Box>
          )}

          {['in_progress', 'completed'].includes(run.status) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 1.5 }}>
              <TextField size="small" label="Void reason" value={voidReason} onChange={(event) => setVoidReason(event.target.value)} />
              <Button color="error" variant="outlined" startIcon={<Close />} disabled={voidReason.trim().length < 10} onClick={voidRun}>
                Void
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
